import * as React from "react"
import {ProductFormState} from "./_reducers"
import {
    IChangeFilePositionPayload,
    IDeleteFilePayload,
    IRestoreFilePayload,
    ProductFormActions,
} from "./_actions"
import {stringify, stringifyTree} from "../../utils/stringifyier"
import {
    $addVariants,
    $getFieldsByFamilyTech,
    $getProduct,
    $getProductPreviousVersions,
    $insertProduct, $sanitizeVariants,
    $updateProduct,
    $validateProduct,
} from "../../core/requests"
import {
    convertToBoolean,
    hasAtLeastSelectedOneDeclination,
    Languages,
    preventExistingIdReferenceCreation,
    Roles,
} from "../../utils"
import {
    checkFieldValidity,
    checkFileProps,
    checkFieldsValidity,
    ITranslations,
    permanentFormIds,
    ISupplierReference,
    IRawSupplierReference,
    ISupplierInfo,
    checkFilePresenceForRestore,
    IHandleChangeProps,
    FieldType,
} from "../../models"
import {
    Button,
    Divider,
    Dropdown,
    DropdownProps,
    Label,
    Popup,
} from "semantic-ui-react"
import PicturesContainer, {
    PicturePreviousVersion,
} from "../components/form-field-file-containers/com_picture-container"
import CommonFields, {
    TechFieldsComponent,
} from "../components/form-field-containers/com_common-fields"
import UncommonFields from "../components/form-field-containers/com_uncommon-fields"
import SideBar from "../components/form-field-sidebar/com_sidebar"
import IVat from "../../models/product/vat"
import ICustoms from "../../models/product/customs"
import ButtonLock from "../components/buttons/com_button-lock"
import {
    IDropdownOption,
    IFieldState,
} from "./_reducers/_utils/product-form-types"
import FileContainer, {
    FilePreviousVersion,
} from "../components/form-field-file-containers/com_file-container"
import ITerritoi from "../../models/product/territoi"
import {createFormFieldByCaratech} from "./_reducers/_utils/create-field"
import {ITreeNode} from "../../utils/tree/interfaces"
import {toastr} from "react-redux-toastr"
import {getFormDataFromFields} from "../../core/actions"
import Loading from "../components/com_loading"
import {
    getFieldDataFromLS,
    purgeFieldDataFromLS,
    storeFieldDataInLS,
} from "../../utils/local-storage-keys"
import {
    IPreviousVersions,
    IStandardFields,
    ITechFields,
} from "./_reducers/form-id-state"
import {
    deleteDoublonComplex,
    haveArraysTheSameOrder,
} from "../../utils/doublon-tools"
import moment from "moment"
import DeclinedFields from "./com_declinedFields"
import DeclinedModal from "./com_declinedModal"
import {ITax, ITechField} from "../../models/bo"
import {StringUtils} from "../../@types/StringUtils";

export interface IProductFormProps {
    fetchProducts: () => void
    actions: ProductFormActions
    state: ProductFormState
    techPropNodes: ITreeNode[]
    techRawData: ITreeNode[]
    comPropNodes: ITreeNode[]
    comRawData: ITreeNode[]
    goToProduct: (formId: string, declined?: string) => void
    goToProductList: () => void
    selectedSupplier: ISupplierInfo
    formId: string
    declinedId?: string
    activeLang: Languages
    role: Roles
    translations: ITranslations["productForm"]
    errorTranslations: ITranslations["fieldErrors"]
    customs: ICustoms[]
    territoi: ITerritoi[]
    brands: string[]
    products: IRawSupplierReference[]
    vat: IVat[]
    warrantyOptionList: IDropdownOption[]
    taxes: ITax[]
}

const inputPicture: any = React.createRef()
const inputPdf: any = React.createRef()

const handleFocusInputPicture = (e: any) => {
    e.preventDefault()
    if (inputPicture && inputPicture.current && inputPicture.current) {
        inputPicture.current.click()
    }
}

const handleFocusInputPdf = (e: any) => {
    e.preventDefault()
    if (inputPdf && inputPdf.current && inputPdf.current) {
        inputPdf.current.click()
    }
}

const ProductForm = ({
                         fetchProducts,
                         state,
                         actions,
                         formId,
                         activeLang,
                         goToProduct,
                         goToProductList,
                         declinedId,
                         products,
                         role,
                         translations,
                         errorTranslations,
                         selectedSupplier,
                         techPropNodes,
                         comPropNodes,
                         techRawData,
                         comRawData,
                         territoi,
                         brands,
                         customs,
                         vat,
                         warrantyOptionList,
                         taxes,
                     }: IProductFormProps) => {
    const [hasBeenSanitized, setHasBeenSanitized] = React.useState(false)
    const [controller, setController] = React.useState<AbortController>()
    const [loadCaratech, setLoadCaratech] = React.useState(false)
    const [hasLoadedLocalStorage, setHasLoadedLocalStorage] =
        React.useState(false)

    const loading = state.loading > 0
    const submitting = state.submitting
    const {
        isInitialized,
        standardFields,
        techFields,
        techFieldsRawData,
        deletedFileNames,
        product,
        previousVersions,
        selectedPreviousVersion,
    } = state.dataByFormId[formId]
    const _previousVersion = selectedPreviousVersion
        ? previousVersions[selectedPreviousVersion]
        : null
    const previousVersion =
        _previousVersion && _previousVersion !== "loading"
            ? _previousVersion
            : undefined
    const allFields: { [fieldName: string]: IFieldState<any> } = {
        ...standardFields,
        ...techFields,
    }
    const {
        pictures,
        pdf,
        isTopRef,
        declination,
        declined,
        declinedToAdd,
        droopyId,
        validated,
    } = standardFields

    const isVariant = React.useMemo(() => {
        if (formId === permanentFormIds.NEW_PRODUCT) {
            return false
        }

        return !!products.find(
            (e) => e.isTopRef && e.supplierRef === product?.topRef
        )
    }, [products, product?.topRef])

    React.useEffect(() => {
        if (formId === permanentFormIds.NEW_PRODUCT
            || !isInitialized
            || techFieldsRawData.length <= 0
            || !product
            || hasBeenSanitized)
            return
        actions._startLoading()
        actions._sanitizeProduct(formId, {
            product,
            techRawData,
            comRawData,
            techFieldsRawData
        })
        tryWarrantyMatch()
        setHasBeenSanitized(true)
    }, [isInitialized, techFieldsRawData])

    React.useEffect(() => {
        if (!hasBeenSanitized)
            return
        $sanitizeVariants(product)
    }, [hasBeenSanitized])

    React.useEffect(() => {
        if (formId !== permanentFormIds.NEW_PRODUCT) {
            setHasLoadedLocalStorage(true)
        } else if (!hasLoadedLocalStorage && !loading && !loadCaratech) {
            loadFieldDataFromLS(FieldType.Standard)
        }
    }, [stringify(formId), loading, loadCaratech])

    React.useEffect(() => {
        if (loadCaratech) {
            return
        }
        if (formId === permanentFormIds.NEW_PRODUCT) {
            loadFieldDataFromLS(FieldType.Tech)
        }
    }, [loadCaratech])

    // React.useEffect(() => {
    //   if (declination.value) {
    //     const field = techFields[declination.value as string]
    //     if (field) {
    //       actions._updateDeclinationOptions(field.options)
    //       if (
    //         hasLoadedLocalStorage &&
    //         field.options !== state.declinationOptions
    //       ) {
    //         handleResetField(declined)
    //       }
    //     }
    //   }
    // }, [stringify(declination.value), loadCaratech])

    React.useEffect(() => {
        actions._updateFieldLabel(formId, translations)
    }, [stringify(translations)])

    React.useEffect(() => {
        if (isInitialized) {
            return
        }
        if (comRawData.length === 0 || techRawData.length === 0) {
            formRef.current?.scrollTo({top: 0, behavior: "smooth"})
            actions._startLoading()
            return
        }
        actions._startLoading()

        const onStorePreviousVersion = (p: ISupplierReference, d: string) =>
            actions._storePreviousVersion(formId, p, d)

        $getProduct(formId, role)
            .then((p) => {
                if (p) {
                    actions._storeProduct(formId, {
                        product: p,
                        comRawData,
                        techRawData,
                    })
                    if (role === Roles.MARKET_LEADER) {
                        const c = new AbortController()
                        setController(c)
                        $getProductPreviousVersions(
                            formId,
                            p.previousVersionDates,
                            "classic",
                            onStorePreviousVersion,
                            c.signal
                        )
                    } else {
                        const d = moment().format("YYYY-MM-DD HH:mm:ss")
                        onStorePreviousVersion(p, d)
                        actions._selectPreviousVersion(formId, d)
                    }
                } else {
                    actions._stopLoading()
                    actions._closeFormId(formId)
                }
            })
            .catch(() => {
                actions._stopLoading()
                actions._closeFormId(formId)
            })
    }, [
        stringify(activeLang),
        stringify(formId),
        stringify(comRawData),
        stringify(techRawData),
        isInitialized,
    ])

    React.useEffect(() => {
        handleChangeFamilyTech()
        if (
            hasLoadedLocalStorage &&
            !state.declinationList.includes(declination.value as any)
        ) {
            handleResetField(declination)
            handleResetField(declined)
        }
    }, [stringifyTree(standardFields.techNode.value)])

    const isNewProduct = formId === permanentFormIds.NEW_PRODUCT
    const pageTitle = !isNewProduct
        ? translations.modifyProductMainTitle
        : translations.addProductMainTitle
    const productAlreadyValidated = convertToBoolean(validated.value)
    let tabIndex = 1

    const loadFieldDataFromLS = (fieldType: FieldType) => {
        const fieldList = Object.keys(
            fieldType === FieldType.Tech ? techFields : standardFields
        )

        fieldList.forEach((code) => {
            const field =
                fieldType === FieldType.Tech ? techFields[code] : standardFields[code]
            const value = getFieldDataFromLS(code, field.value)

            actions._onChangeField(formId, {
                code,
                fieldValue: value,
                fieldType: field.fieldType,
                lock: field.lock,
                errorMessage: field.errorText,
                overrideDefaultValue: false,
            })
        })

        setHasLoadedLocalStorage(true)
    }

    const tryWarrantyMatch = () => {
        if (!standardFields.supplierWarranty.value)
            return
        if (standardFields.bruneauWarranty.value !== "-1"
            && standardFields.bruneauWarranty.value !== -1
            && standardFields.bruneauWarranty.value !== "-"
            && standardFields.bruneauWarranty.value)
            return
        const list = warrantyOptionList
            .map(r => ({
                score: StringUtils.compareSimilarityPercent(r.text, standardFields.supplierWarranty.value),
                value: r
            }))
            .sort((a, b) => b.score - a.score)
        const found = list
            .find(x => x.value.text[0] === standardFields.supplierWarranty.value[0])
        if (!found || found.score < 65)
            return

        handleChangeField({
            code: standardFields.bruneauWarranty.code,
            fieldType: FieldType.Standard,
            lock: "unlock",
            fieldValue: found.value.value,
            overrideDefaultValue: true
        }, true)
    }

    const handleResetField = (field: IFieldState<any>) => {
        actions._onChangeField(formId, {
            code: field.code,
            fieldValue: field.regenerateInfos.defaultValue,
            fieldType: field.fieldType,
            lock: field.lock,
            errorMessage: "",
            overrideDefaultValue: true,
        })
    }

    const handleChangeField = (
        {
            code,
            fieldType,
            lock,
            fieldValue,
            overrideDefaultValue = false,
            forceAcceptEmptyArray = false,
            allowDefaultValue = false,
        }: IHandleChangeProps,
        ignoreError = false
    ) => {
        const field =
            fieldType === FieldType.Standard ? standardFields[code] : techFields[code]
        if (
            field !== undefined &&
            ((fieldValue !== undefined && fieldValue !== field.value) ||
                lock !== field.lock)
        ) {
            const value =
                (fieldValue !== undefined || allowDefaultValue) &&
                (fieldValue !== null || allowDefaultValue) &&
                (fieldValue !== "-1" || allowDefaultValue) &&
                (stringify(fieldValue) !== "[]" || forceAcceptEmptyArray)
                    ? fieldValue
                    : field.value

            const errorMessage = (ignoreError || (code == "ean" && !selectedSupplier.eanMandatory))
                ? ""
                : checkFieldValidity({
                    field: {...field, value},
                    translations: errorTranslations,
                })

            if (hasLoadedLocalStorage && formId === permanentFormIds.NEW_PRODUCT) {
                storeFieldDataInLS(code, value)
            }

            actions._onChangeField(formId, {
                code,
                fieldValue: value,
                fieldType,
                lock,
                errorMessage,
                overrideDefaultValue,
            })
        }
    }

    const handleFieldLockClick = (event: any, fieldType: FieldType) => {
        event.preventDefault()

        handleChangeField({
            code: event.currentTarget.name,
            fieldType,
            lock: event.currentTarget.value === "lock" ? "unlock" : "lock",
        })
    }

    const handleChangeFamilyTech = () => {
        // @ts-ignore
        // tslint:disable-next-line: triple-equals
        if (standardFields.techNode.value.id == 0) {
            return
        }

        setLoadCaratech(true)
        $getFieldsByFamilyTech(standardFields.techNode.value.id)
            .then((response) => {
                const techF = response.map((rawData) =>
                    createFormFieldByCaratech(rawData)
                )
                const declinationOptions = response
                    .filter((x) => x.isDeclinable && x.type === "select")
                    .map((x) => ({text: x.label, key: x.codeEnum, value: x.codeEnum}))
                actions._updateDeclinationList(declinationOptions)

                if (standardFields.techNode.value.code === product?.techNode) {
                    actions._storeTechFields(formId, {
                        fields: techF,
                        rawData: response,
                        techValues: product.techFields,
                    })
                } else {
                    actions._storeTechFields(formId, {fields: techF, rawData: response})
                }
            })
            .finally(() => setLoadCaratech(false))
    }

    const handleChangeFile: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        if (e.target?.files?.length === 0) {
            return
        }
        actions._startLoading()
        const code = e.target?.name
        const files = standardFields[code].value

        const queue = Array.from(e.target?.files || [])
        queue.forEach((file: File) => {
            if (checkFileProps(file, files, code, translations)) {
                const reader = new FileReader()
                reader.onloadend = () => {
                    actions._createFile(formId, {
                        code,
                        file,
                        blob: reader.result,
                        name: file.name,
                        isNew: true,
                    })
                }
                reader.readAsDataURL(file)
            }
        })
        actions._stopLoading()
    }

    const handleDeleteFile = (payload: IDeleteFilePayload) =>
        actions._deleteFile(formId, payload)

    const handleRestoreFile = (payload: IRestoreFilePayload) => {
        const files = standardFields[payload.code].value
        if (checkFilePresenceForRestore(payload.fileInfos, files, translations)) {
            actions._restoreFile(formId, payload)
        }
    }

    const handleChangeFilePosition = (payload: IChangeFilePositionPayload) =>
        actions._changeFilePosition(formId, payload)

    const isProductValid = (): boolean => {
        const fields = {...standardFields, ...techFields}
        const notRequiredList = [
            isTopRef.value && formId === permanentFormIds.NEW_PRODUCT
                ? "declined"
                : "declined",
            isTopRef.value && formId === permanentFormIds.NEW_PRODUCT
                ? "declination"
                : "declination",
            isTopRef.value || !selectedSupplier.eanMandatory ? "ean" : "",
            isTopRef.value ? "manufacturerRef" : "",
            isTopRef.value ? "supplierRef" : "",
            "declinedToAdd",
            "brand",
        ]
        const errorMessages = checkFieldsValidity({
            fields,
            notRequiredList,
            translations: {...translations, ...errorTranslations},
        })
        actions._setErrorMessages(formId, errorMessages)

        if (Object.keys(errorMessages).length > 0) {
            const errorLabels = Object.keys(errorMessages).map(
                (e) => allFields[e].label || e
            )
            toastr.error(
                "Le(s) champ(s) suivant(s) sont en erreur :",
                errorLabels.join(", ")
            )
            return false
        }

        if (
            isTopRef.value &&
            formId === permanentFormIds.NEW_PRODUCT &&
            !hasAtLeastSelectedOneDeclination(techFields)
        ) {
            toastr.error("Vous devez renseigner une déclinaison", "")
            return false
        }

        return true
    }

    const areIdsFree = (): boolean => {
        if (
            !isTopRef.value &&
            // isNewProduct &&
            preventExistingIdReferenceCreation(
                products,
                standardFields.ean.value,
                standardFields.supplierRef.value,
                formId
            )
        ) {
            toastr.error(translations.productAlreadyCreated, "")
            return false
        }
        return true
    }

    const handleSubmit = (event: React.SyntheticEvent) => {
        event.preventDefault()
        registerProduct()
    }

    const registerProduct = (isValidation = false) => {
        if (!isProductValid() || !areIdsFree()) {
            return new Promise((re) => re(""))
        }

        const caraTechOptions = Object.keys(techFields).reduce((acc, k) => {
            const o = techFields[k].options
            return o.length > 0 ? {...acc, [k]: o} : acc
        }, {} as { [key: string]: IDropdownOption[] })

        actions._startSubmitting()
        controller?.abort()
        formRef.current?.scrollTo({top: 0, behavior: "smooth"})
        const form = getFormDataFromFields(
            standardFields,
            techFields,
            techFieldsRawData,
            caraTechOptions,
            role,
            deletedFileNames
        )

        if (standardFields.droopyId.value) {
            return $updateProduct(
                form,
                product?.supplierRef || standardFields.supplierRef.value
            )
                .then((res) => {
                    return isTopRef.value
                        ? $addVariants(product, techFields).then((x) => res.json())
                        : []
                    // : res.json()
                })
                .then((r) => {
                    if (!isValidation) {
                        toastr.success(translations.registerProductSuccess, "")
                        if (r.length > 0) {
                            actions._refetchProductData(formId)
                            setHasBeenSanitized(false)
                            goToProduct(formId)
                        } else {
                            actions._closeFormId(formId)
                        }
                    }
                })
                .catch((r) => toastr.error(translations.cantFetchServer, ""))
                .finally(() => actions._stopSubmitting())
        } else {
            return $insertProduct(
                form,
                techFields
                // product?.supplierRef || standardFields.supplierRef.value
            )
                .then((res) => res.json())
                .then((r) => {
                    toastr.success(translations.registerProductSuccess, "")
                    handleResetForm()
                    if (isTopRef.value) {
                        goToProduct(r)
                    } else {
                        goToProductList()
                    }
                })
                .catch((r) => toastr.error(translations.cantFetchServer, ""))
                .finally(() => actions._stopSubmitting())
        }
    }

    const handleResetForm = (e?: React.SyntheticEvent) => {
        e?.preventDefault()
        const locked = Object.keys(allFields).map((f) =>
            allFields[f].lock === "lock" ? f : ""
        )
        actions._resetForm(formId, {locked, previousVersions})
        setHasBeenSanitized(false)
        if (product && comRawData.length > 0 && techRawData.length > 0) {
            actions._storeProduct(formId, {product, comRawData, techRawData})
        }
        if (formId === permanentFormIds.NEW_PRODUCT) {
            purgeFieldDataFromLS()
        }
    }

    const handleValidateProduct = (event: React.SyntheticEvent) => {
        event.preventDefault()
        if (!isProductValid() || !areIdsFree()) {
            return
        }
        actions._startLoading()
        $validateProduct(formId, role, null, selectedSupplier.supplierName).then(
            () => {
                actions._stopLoading()
                fetchProducts()
                actions._closeFormId(formId)
            }
        )
    }

    const placeholder = (source: string) => () =>
        console.log("placeholder" + source)

    const onFieldLooseFocus = placeholder("onFieldLooseFocus")
    const loadingText =
        declined.value &&
        (declined.value as []).length > 0 &&
        isTopRef.value &&
        translations.loadingDeclinedText &&
        submitting
            ? translations.loadingDeclinedText.replace(
                "{{value}}",
                (declined.value as []).length + 1 + ""
            )
            : ""

    const formRef = React.useRef<HTMLDivElement>(null)
    const closeDeclinedModal = () => goToProduct(formId)
    const savedDeclinedModal = () => {
        // if (product && product.declined) {
        //   const newProduct = { ...product }
        //   newProduct.declined = product.declined.map((x) => {
        //     if (x.droopyId !== declinedId) {
        //       return x
        //     }
        //     return {
        //       ...x,
        //       techFields: x.techFields.map((t) => {
        //         if (tech[t.codeEnum]) {
        //           return {
        //             ...t,
        //             value: tech[t.codeEnum].value,
        //             valueCode: tech[t.codeEnum].value,
        //           }
        //         }
        //         return t
        //       }),

        //     }
        //   })
        // }
        actions._refetchProductData(formId)
        setHasBeenSanitized(false)
        goToProduct(formId)
    }

    return (
        <div className="product-view">
            {isTopRef.value && (
                <DeclinedModal
                    activeLang={activeLang}
                    errorTranslations={errorTranslations}
                    products={products}
                    role={role}
                    selectedSupplier={selectedSupplier}
                    translations={translations}
                    techFieldsRawData={techFieldsRawData}
                    topRef={product}
                    declinedDroopyId={declinedId}
                    onCloseWithoutSave={closeDeclinedModal}
                    onCloseWithSave={savedDeclinedModal}
                />
            )}
            <h1 className="page-title">{pageTitle}</h1>
            <form onSubmit={handleSubmit} className="product-form">
                <div className="product-form-div">
                    <div
                        className="inputs-container"
                        ref={formRef}
                        style={loading ? {overflow: "hidden"} : undefined}
                    >
                        {loading && <Loading text={loadingText}/>}
                        {hasLoadedLocalStorage && (hasBeenSanitized || isNewProduct) && (
                            <DeclinedFields
                                isNewProduct={isNewProduct}
                                declinedList={product?.declined}
                                role={role}
                                activated={standardFields.activated}
                                isTopRef={isTopRef}
                                product={product}
                                ean={standardFields.ean}
                                supplierRef={standardFields.supplierRef}
                                manufacturerRef={standardFields.manufacturerRef}
                                goToProduct={goToProduct}
                                techNode={standardFields.techNode}
                                techTreeState={standardFields.techTreeState}
                                actions={actions}
                                formId={formId}
                                techPropNodes={techPropNodes}
                                tabIndex={tabIndex}
                                translations={translations}
                                handleChangeField={handleChangeField}
                                onFieldLockClick={handleFieldLockClick}
                                techFieldsIsLoading={loadCaratech}
                                techFields={techFields}
                                techRawData={techFieldsRawData}
                                activeLang={activeLang}
                                onFieldLooseFocus={onFieldLooseFocus}
                                previousVersion={previousVersion}
                                selectedPreviousVersion={selectedPreviousVersion}
                            />
                        )}
                        <div className="form-inputs">
                            <h2 className="form-title"> {translations.caracTitle} </h2>
                            <UncommonFields
                                lockable={true}
                                previousVersion={previousVersion}
                                activeLang={activeLang}
                                tabIndex={tabIndex++}
                                isNewProduct={isNewProduct}
                                translations={translations}
                                boolTopRef={isTopRef.value}
                                {...standardFields}
                                handleChangeStandardField={handleChangeField}
                                handleStandardFieldLockClick={handleFieldLockClick}
                                onFieldLooseFocus={onFieldLooseFocus}
                                selectedPreviousVersion={selectedPreviousVersion}
                            />
                            <CommonFields
                                supplier={selectedSupplier}
                                lockable={true}
                                previousVersion={previousVersion}
                                selectedPreviousVersion={selectedPreviousVersion}
                                tabIndex={tabIndex + 3}
                                isVariant={isVariant}
                                isMarketLeader={role === Roles.MARKET_LEADER}
                                comNodeDisabledMessage={translations.comNodeDisabledVariant}
                                techNodeDisabledMessage={translations.techNodeDisabledVariant}
                                techFieldsIsLoading={loadCaratech}
                                vatOptionList={vat}
                                warrantyOptionList={warrantyOptionList}
                                taxesList={taxes}
                                customsOptionList={customs}
                                territoiOptionList={territoi}
                                brandOptionList={brands}
                                handleChangeField={handleChangeField}
                                onFieldLockClick={handleFieldLockClick}
                                onFieldLooseFocus={onFieldLooseFocus}
                                {...{
                                    standardFields,
                                    techFields,
                                    techRawData: techFieldsRawData,
                                    comPropNodes,
                                    techPropNodes,
                                    translations,
                                    activeLang,
                                    formId,
                                }}
                            />
                        </div>
                    </div>
                    <div className="pictures-container">
                        {loading && (
                            <Loading style={{marginLeft: "-2em", width: "105%"}}/>
                        )}
                        <div>
                            <input
                                ref={inputPicture}
                                type="file"
                                accept="image/*"
                                name="pictures"
                                onChange={handleChangeFile}
                                style={{display: "none"}}
                                multiple
                            />
                        </div>
                        <div>
                            <Label
                                as="a"
                                className="button colored-button"
                                ribbon
                                onClick={handleFocusInputPicture}
                            >
                                {translations.addImageButtonLabel}
                            </Label>
                            <ButtonLock
                                hidden={!isNewProduct}
                                classes={{lock: "file-lock", unlock: "file-unlock"}}
                                lockable={true}
                                onClick={handleFieldLockClick}
                                code={pictures.code}
                                lock={pictures.lock}
                                fieldType={FieldType.Standard}
                            />
                            {previousVersion &&
                                selectedPreviousVersion &&
                                !haveArraysTheSameOrder(
                                    deleteDoublonComplex(previousVersion.pictures, "name"),
                                    pictures.value,
                                    "name"
                                ) &&
                                PicturePreviousVersion(
                                    pictures.code,
                                    handleRestoreFile,
                                    selectedPreviousVersion,
                                    previousVersion
                                )}
                        </div>

                        <PicturesContainer
                            onDeleteFile={handleDeleteFile}
                            onChangeFilePosition={handleChangeFilePosition}
                            code={pictures.code}
                            files={pictures}
                            error={pictures.errorText}
                        />
                        <div>
                            <input
                                ref={inputPdf}
                                accept="application/pdf"
                                type="file"
                                name="pdf"
                                onChange={handleChangeFile}
                                style={{display: "none"}}
                                multiple
                            />
                        </div>
                        <div style={{position: "relative"}}>
                            <Label
                                as="a"
                                className="button colored-button"
                                ribbon
                                onClick={handleFocusInputPdf}
                            >
                                {translations.addPdfButtonLabel}
                            </Label>
                            <ButtonLock
                                hidden={!isNewProduct}
                                lockable={true}
                                classes={{lock: "file-lock", unlock: "file-unlock"}}
                                onClick={handleFieldLockClick}
                                code={pdf.code}
                                lock={pdf.lock}
                                fieldType={FieldType.Standard}
                            />
                            {previousVersion &&
                                selectedPreviousVersion &&
                                !haveArraysTheSameOrder(
                                    deleteDoublonComplex(previousVersion.pdf, "name"),
                                    pdf.value,
                                    "name"
                                ) &&
                                FilePreviousVersion(selectedPreviousVersion, previousVersion)}
                        </div>

                        <FileContainer
                            onDeleteFile={handleDeleteFile}
                            onChangeFilePosition={handleChangeFilePosition}
                            code={pdf.code}
                            files={pdf}
                            error={pdf.errorText}
                        />
                    </div>
                </div>
                <Divider fitted/>
                <div className="form-buttons">
                    <CommonButtons
                        product={product}
                        translations={translations}
                        handleResetForm={handleResetForm}
                        loading={loading}
                        loadCaratech={loadCaratech}
                        isNewProduct={isNewProduct}
                    />
                    <MarketLeaderButtons
                        role={role}
                        product={product}
                        translations={translations}
                        handleValidateProduct={handleValidateProduct}
                        selectedPreviousVersion={selectedPreviousVersion}
                        previousVersions={previousVersions}
                        isNewProduct={isNewProduct}
                        productAlreadyValidated={productAlreadyValidated}
                        actions={actions}
                        formId={formId}
                    />
                </div>
            </form>

            <SideBar actualDroopyId={droopyId.value} activeLang={activeLang}/>
        </div>
    )
}

interface ISafeGuardPopupProps {
    disabled: boolean
    translations: ITranslations["productForm"]
}

const SafeGuardPopup = (
    props: React.PropsWithChildren<ISafeGuardPopupProps>
) => (
    <Popup
        position="top center"
        trigger={props.children}
        disabled={props.disabled}
        cenete
        content={
            <div style={{textAlign: "center"}}>
                {props.translations.cannotInteractWithDeletedProduct}
            </div>
        }
    />
)

interface IMarketLeaderButtons {
    role: Roles
    product: ISupplierReference | null
    translations: ITranslations["productForm"]
    handleValidateProduct: (event: React.SyntheticEvent) => void
    isNewProduct: boolean
    productAlreadyValidated: boolean
    previousVersions: IPreviousVersions
    selectedPreviousVersion: string | null
    actions: ProductFormActions
    formId: string
}

const MarketLeaderButtons = ({
                                 role,
                                 product,
                                 translations,
                                 handleValidateProduct,
                                 previousVersions,
                                 selectedPreviousVersion,
                                 isNewProduct,
                                 productAlreadyValidated,
                                 actions,
                                 formId,
                             }: IMarketLeaderButtons) => {
    const previousVersionOptions = React.useMemo(
        () =>
            product?.previousVersionDates?.reduce(
                (acc, d) => [
                    ...acc,
                    {
                        key: d,
                        value: d,
                        text: new Date(d).toLocaleDateString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                        }),
                        selected: d === selectedPreviousVersion,
                    },
                ],
                [] as Array<{ key: string; value: string; text: string }>
            ) || [
                {key: "no-date", value: "no-date", text: "Aucune version précédente"},
            ],
        [product?.previousVersionDates]
    )

    const onChange = (_: any, b: DropdownProps) => {
        actions._selectPreviousVersion(formId, b.value + "")
    }

    const text =
        product?.previousVersionDates && product?.previousVersionDates.length > 0
            ? new Date(selectedPreviousVersion + "").toLocaleDateString([], {
                hour: "2-digit",
                minute: "2-digit",
            })
            : "Aucune version précédente"

    return (
        <div className="market-leader-buttons">
            {role === Roles.MARKET_LEADER ? (
                <>
                    <SafeGuardPopup
                        disabled={!(product && product.deleted)}
                        translations={translations}
                    >
                        <div>
                            <Button
                                onClick={handleValidateProduct}
                                className="element button marketLeaderButton"
                                disabled={
                                    isNewProduct ||
                                    productAlreadyValidated ||
                                    !!(product && product.deleted)
                                }
                            >
                                {productAlreadyValidated
                                    ? translations.alreadyValidatedProductCdmLabel
                                    : translations.validateProductCdmLabel}
                            </Button>
                        </div>
                    </SafeGuardPopup>
                    <SafeGuardPopup
                        disabled={!(product && product.deleted)}
                        translations={translations}
                    >
                        <div className="previous-version-dropdown">
              <span>
                Comparer avec{" "}
                  <Dropdown
                      inline
                      loading={
                          previousVersions[selectedPreviousVersion + ""] ===
                          ("loading" || undefined)
                      }
                      options={previousVersionOptions}
                      value={selectedPreviousVersion || "no-date"}
                      onChange={onChange}
                      text={text}
                      selectOnBlur={false}
                      selectOnNavigation={false}
                  />
              </span>
                        </div>
                    </SafeGuardPopup>
                </>
            ) : (
                ""
            )}
        </div>
    )
}

interface ICommonButtons {
    product: ISupplierReference | null
    translations: ITranslations["productForm"]
    handleResetForm: (
        e?: React.SyntheticEvent<Element, Event> | undefined
    ) => void
    loading: boolean
    loadCaratech: boolean
    isNewProduct: boolean
}

const CommonButtons = ({
                           product,
                           translations,
                           handleResetForm,
                           loading,
                           loadCaratech,
                           isNewProduct,
                       }: ICommonButtons) => {
    return (
        <div className="common-buttons">
            <SafeGuardPopup
                disabled={!(product && product.deleted)}
                translations={translations}
            >
                <div>
                    <Button
                        type="submit"
                        disabled={!!(product && product.deleted) || loadCaratech || loading}
                        className="element button filled colored-button"
                    >
                        {translations.validationButtonLabel}
                    </Button>
                </div>
            </SafeGuardPopup>
            <SafeGuardPopup
                disabled={!(product && product.deleted)}
                translations={translations}
            >
                <div>
                    <Button
                        onClick={handleResetForm}
                        className="element button colored-button"
                        disabled={!!(product && product.deleted) || loading}
                    >
                        {!isNewProduct
                            ? translations.cancelModification
                            : translations.resetButtonLabel}
                    </Button>
                </div>
            </SafeGuardPopup>
        </div>
    )
}

export default ProductForm
